// ─── Polyfill: crypto.randomUUID for insecure contexts ───────────────────────
// Browsers expose `crypto.randomUUID()` ONLY in secure contexts (HTTPS or
// localhost). When the app is served over plain HTTP on a LAN IP — e.g.
// http://192.168.30.100:5174, which is how on-prem users actually connect —
// `crypto.randomUUID` is `undefined`, so any call site using it throws a
// TypeError. Dozens of places rely on it (tag creation, story creation, cuts,
// selection ids…), so a missing UUID silently breaks buttons ("nothing happens").
//
// `crypto.getRandomValues()` is NOT gated behind secure contexts, so we can
// build a spec-compliant v4 UUID from it and install it as a fallback. Native
// `randomUUID` (secure contexts) is left untouched.
function uuidv4FromGetRandomValues(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return (
    hex.slice(0, 4).join("") +
    "-" +
    hex.slice(4, 6).join("") +
    "-" +
    hex.slice(6, 8).join("") +
    "-" +
    hex.slice(8, 10).join("") +
    "-" +
    hex.slice(10, 16).join("")
  );
}

if (typeof crypto !== "undefined" && typeof crypto.randomUUID !== "function") {
  try {
    Object.defineProperty(crypto, "randomUUID", {
      value: uuidv4FromGetRandomValues,
      writable: true,
      configurable: true,
    });
  } catch {
    // Some engines expose a non-configurable Crypto instance; attempt a plain
    // assignment as a last resort.
    try {
      (crypto as unknown as { randomUUID: () => string }).randomUUID = uuidv4FromGetRandomValues;
    } catch {
      // Nothing more we can do; call sites already guard in a few spots.
    }
  }
}

export {};
